import { CSSProperties } from 'react'
import { getCompany } from '../../../../../../../../utils/reducers/getters'
import { PdfConfigAllModules, PdfConfigTemplateAllModulesKey, PdfDrawLineElement, PdfLogoElement, PdfTableElement, PdfTextElement } from '../../types'
import usePdfRendererStyles from '../usePdfRendererStyles'

interface PdfModulesRendererProps {
    mymodule: PdfConfigAllModules
}

const PdfModulesRenderer: React.FC<PdfModulesRendererProps> = ({mymodule}) => {
    const dict: Record<PdfConfigTemplateAllModulesKey, React.FC<any>> = {
        Text: TextModule,
        Logo: LogoModule,
        Table: TableModule,
        TableRecursive: TableRecursiveModule,
        DrawLine: DrawLineModule,
        PrintBarcode: TextModule,
        PrintQR: TextModule
    }
    const Component = dict[mymodule.component]
    const { renderStyles } = usePdfRendererStyles()
    const myStylesRenderer = renderStyles(mymodule.cssConfig)
    return <Component mymodule={{...mymodule, cssConfig: myStylesRenderer}} />
}

interface TextModuleProps {
    mymodule: PdfTextElement
}

const TextModule: React.FC<TextModuleProps> = ({ mymodule }) => {
    const height = mymodule?.cssConfig?.height || 'fit-content'
    return (
        <span style={{ whiteSpace: 'pre-wrap', textAlign: 'left', ...mymodule.cssConfig, height }}>{mymodule.extraProps?.text}</span>
    )
}

interface LogoModuleProps {
    mymodule: PdfLogoElement
}

const LogoModule: React.FC<LogoModuleProps> = ({ mymodule }) => {
    const image = getCompany()?.['image']
    const height = mymodule?.cssConfig?.height || 'fit-content'
    return (<img src={image} alt="" style={{ ...mymodule.cssConfig, height }}/>)
}

interface TableRecursiveModuleProps {
    mymodule: PdfTableElement
}

const TableRecursiveModule: React.FC<TableRecursiveModuleProps> = ({ mymodule }) => {
    return (<TableModule mymodule={{ ...mymodule, extraProps: { ...mymodule.extraProps, nRows: 2,} }} recursive={true} />)
}

interface TableModuleProps {
    mymodule: PdfTableElement
    recursive?: boolean
}

const TableModule: React.FC<TableModuleProps> = ({ mymodule, recursive = false }) => {
    const { renderStyles } = usePdfRendererStyles()
    const props = mymodule.extraProps
    if (!parseInt(String(props.nCols)) || !parseInt(String(props.nRows))) {
        return null
    }
    let myRows = props.nRows
    let myCols = props.nCols
    const renderThis: JSX.Element[] = []
    let columnWidthSum = 0
    let leftColums = myCols
    let skipWidthSince = myCols +1
    if (props.columnsWidth) {
        for (const key in props.columnsWidth) {
            if (props.columnsWidth[key] !== '') {
                const element = parseInt(props.columnsWidth[key])
                if ((columnWidthSum + element) > 100) {
                    skipWidthSince = parseInt(key)
                    break
                }
                columnWidthSum += element
                leftColums--
            }
        }
    }
    let columnWidth = (100 - columnWidthSum ) / leftColums
    for (let keyRow = 0; keyRow < myRows; keyRow++) {
        const row: JSX.Element[] = []
        let style: CSSProperties = {
            minHeight: props.heightRow,
            borderBottom: props.cellBorderBottom,
            display: 'table-row'
        }
        for (let keyCol = 0; keyCol < myCols; keyCol++) {
            const isHeader = props.typeTable === 'vertical' ? keyRow === 0 : keyCol === 0
            const isArrayBackground = Array.isArray(props?.backgroundCellColor?.[keyCol])
            const isArrayText = Array.isArray(props?.textCellColor?.[keyCol])
            const isArrayTextAlign = Array.isArray(props?.textJustifyCell?.[keyCol])
            const isArrayFontWeight = Array.isArray(props?.fontWeightCell?.[keyCol])
            const backgroundCellColor = isArrayBackground ? props?.backgroundCellColor?.[keyCol]?.[keyRow] : props?.backgroundCellColor?.[keyCol] || mymodule?.cssConfig?.background||''
            const textCellColor = isArrayText ? props?.textCellColor?.[keyCol]?.[keyRow] : props?.textCellColor?.[keyCol] || mymodule?.cssConfig?.color || ''
            const textJustifyCell = isArrayTextAlign ? props?.textJustifyCell?.[keyCol]?.[keyRow] : props?.textJustifyCell?.[keyCol] || ''
            const fontWeightCell = isArrayFontWeight ? props?.fontWeightCell?.[keyCol]?.[keyRow] : props?.fontWeightCell?.[keyCol] || mymodule?.cssConfig?.fontWeight || ''
            let style: CSSProperties = {
                padding: props.cellPadding,
                alignItems: props.cellAlignItems,
                justifyContent: props?.textJustifyCell ? textJustifyCell : props.cellJustifyItems,
                display: 'table-cell',
                borderRight: props.cellBorderRight,
                minHeight: props.heightRow,
                backgroundColor: backgroundCellColor,
                color: textCellColor,
                fontWeight: fontWeightCell,
                overflow: 'hidden'
            }
            if (isHeader) {
                style = renderStyles({
                    ...style,
                    alignItems: props.headerAlignItems,
                    justifyContent: textJustifyCell || props.headerJustifyItems,
                    fontSize: props.headerFontSize,
                    fontWeight: props.headerFontWeight || fontWeightCell,
                    backgroundColor: props.headerBackground || backgroundCellColor,
                    color: props.headerColor || textCellColor,
                    headerTicketBorder: props.headerTicketBorder,
                    fontSizeTicket: props.headerFontSizeTicket
                })
            } else {
                style = renderStyles(style)
            }
            const width = keyCol >= skipWidthSince ? columnWidth : props.columnsWidth?.[keyCol]
            row.push(<div key={keyRow + keyCol} style={{ width: (width || columnWidth) + '%', ...style }}>{props.data[keyRow][keyCol]}</div>)
        }
        renderThis.push(<div key={keyRow} style={style}>{row}</div>)
    }
    const height = mymodule?.cssConfig?.height || 'fit-content'
    const width = mymodule?.cssConfig?.width || '100%'
    const moduleCssParsed = renderStyles(mymodule.cssConfig)
    return (
        <div style={{ ...moduleCssParsed, height, width, display: 'table', borderCollapse: 'collapse' }}>
            {renderThis}
            <div style={{display: 'table-row', height: '100%'}}></div>
        </div>
    )
}

interface DrawLineModuleProps {
    mymodule: PdfDrawLineElement
}

const DrawLineModule: React.FC<DrawLineModuleProps> = ({ mymodule }) => {
    return (<div className='w-full border-dashed border-black border' ></div>)
}


export default PdfModulesRenderer